import { useState, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useMcpUsage } from '@/lib/query/hooks/useMcpUsage';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { McpUsageGroup } from '@/lib/types/ai/mcpUsage';

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** バイト数を人間可読に整形 */
function formatBytes(bytes: number): string {
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(2)} TB`;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(2)} MB`;
  if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(2)} KB`;
  return `${bytes} B`;
}

/** 原価を $ 表記で整形 */
function formatCost(value: number): string {
  return `$${value.toFixed(4)}`;
}

const metricCardSx = {
  p: 2,
  textAlign: 'center' as const,
  transition: 'box-shadow 0.2s ease',
  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
};

const metricValueSx = {
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums' as const,
  letterSpacing: '-0.02em',
};

const axisTickStyle = { fontSize: 11, fill: '#94a3b8' };

const chartTooltipStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  padding: '8px 12px',
};

/** サマリーカード 1 枚 */
function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
      <Paper sx={metricCardSx}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={metricValueSx}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </Paper>
    </Grid>
  );
}

/** 軸別集計テーブル (アカウント別 / 関数別 共通) */
function GroupTable({
  title,
  label,
  rows,
}: {
  title: string;
  label: string;
  rows: McpUsageGroup[];
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" mb={1}>
        {title}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{label}</TableCell>
              <TableCell align="right">クエリ数</TableCell>
              <TableCell align="right">走査量</TableCell>
              <TableCell align="right">原価(試算)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((g) => (
              <TableRow key={g.key || '(なし)'}>
                <TableCell>{g.key || '(なし)'}</TableCell>
                <TableCell align="right">{g.query_count.toLocaleString()}</TableCell>
                <TableCell align="right">{formatBytes(g.scanned_bytes)}</TableCell>
                <TableCell align="right">{formatCost(g.estimated_cost_usd)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  データがありません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export function McpUsagePage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // デフォルト: 過去30日
  const defaultEnd = useMemo(() => formatDate(new Date()), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDate(d);
  }, []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  // 顧客がデフォルト
  const [callerType, setCallerType] = useState('customer');
  const [accountCode, setAccountCode] = useState('');
  const [companyId, setCompanyId] = useState('');

  // 粒度は期間の長さから自動決定する (短期=日 / 中期=週 / 長期=月)。
  const granularity = useMemo(() => {
    const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000;
    if (days <= 60) return 'day';
    if (days <= 365) return 'week';
    return 'month';
  }, [startDate, endDate]);

  const {
    data,
    isLoading: dataLoading,
    error: dataError,
  } = useMcpUsage({
    start_date: startDate,
    end_date: endDate,
    caller_type: callerType,
    granularity,
    account_code: accountCode || undefined,
    company_id: companyId || undefined,
  });

  // 選択肢はレスポンス由来 (期間・種別で絞った全件)。企業は全企業、アカウントは選択企業で絞った全件。
  const companyIds = useMemo(() => data?.company_options ?? [], [data?.company_options]);
  const accountCodes = useMemo(() => data?.account_options ?? [], [data?.account_options]);

  if (authLoading) {
    return (
      <AppLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <AppLayout>
        <Alert severity="warning">この機能を利用するにはログインが必要です。</Alert>
      </AppLayout>
    );
  }

  const successRate =
    data && data.summary.query_count > 0
      ? ((data.summary.succeeded_count / data.summary.query_count) * 100).toFixed(1)
      : '0.0';

  return (
    <AppLayout title="MCP利用状況">
      <Box sx={{ p: 3 }}>
        {/* フィルタバー */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              select
              size="small"
              label="種別"
              value={callerType}
              onChange={(e) => setCallerType(e.target.value)}
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="customer">顧客</MenuItem>
              <MenuItem value="internal">社内</MenuItem>
              <MenuItem value="all">すべて</MenuItem>
            </TextField>
            <TextField
              type="date"
              size="small"
              label="開始日"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Typography>〜</Typography>
            <TextField
              type="date"
              size="small"
              label="終了日"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              select
              size="small"
              label="企業"
              value={companyId}
              onChange={(e) => {
                // 企業を変えるとアカウント候補が変わるため選択をリセットする。
                setCompanyId(e.target.value);
                setAccountCode('');
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">すべて</MenuItem>
              {companyIds.map((id) => (
                <MenuItem key={id} value={id}>
                  {id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="アカウント"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">すべて</MenuItem>
              {accountCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        {dataLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {dataError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            MCP利用状況の取得に失敗しました。
          </Alert>
        )}

        {data && (
          <Grid container spacing={3}>
            {/* サマリーカード */}
            <Grid size={12}>
              <Grid container spacing={2}>
                <SummaryCard label="クエリ数" value={data.summary.query_count.toLocaleString()} />
                <SummaryCard label="走査量" value={formatBytes(data.summary.scanned_bytes)} />
                <SummaryCard
                  label="原価(試算 $5/TB)"
                  value={formatCost(data.summary.estimated_cost_usd)}
                />
                <SummaryCard label="ユーザー数" value={data.summary.user_count.toLocaleString()} />
                <SummaryCard label="成功率" value={`${successRate}%`} />
                <SummaryCard
                  label="成功 / 失敗"
                  value={`${data.summary.succeeded_count.toLocaleString()} / ${data.summary.failed_count.toLocaleString()}`}
                />
              </Grid>
            </Grid>

            {/* 期間別クエリ数チャート */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" mb={2}>
                  期間別クエリ数
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.buckets}>
                    <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="bucket"
                      tick={axisTickStyle}
                      tickFormatter={(v) => String(v).slice(5)}
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={axisTickStyle}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value: number) => [value.toLocaleString(), 'クエリ数']}
                      labelFormatter={(label) => `期間: ${label}`}
                    />
                    <Bar
                      dataKey="query_count"
                      fill="#6366f1"
                      name="クエリ数"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* アカウント別走査量チャート */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" mb={2}>
                  アカウント別走査量
                </Typography>
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(300, data.by_account.length * 36)}
                >
                  <BarChart data={data.by_account} layout="vertical">
                    <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => formatBytes(Number(v))}
                      tick={axisTickStyle}
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="key"
                      width={140}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value: number) => [formatBytes(value), '走査量']}
                      cursor={{ fill: 'rgba(99, 102, 241, 0.04)' }}
                    />
                    <Bar
                      dataKey="scanned_bytes"
                      fill="#6366f1"
                      name="走査量"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* ツール(関数)別テーブル */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <GroupTable title="ツール(関数)別" label="関数" rows={data.by_function} />
            </Grid>

            {/* アカウント別テーブル */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <GroupTable title="アカウント別" label="アカウント" rows={data.by_account} />
            </Grid>
          </Grid>
        )}
      </Box>
    </AppLayout>
  );
}
